import type { Word, LearningLanguage } from '../types';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import { getSourceWordLabel } from '../utils/learningLanguage';

interface WordPickerModalProps {
  category: string | null;
  words: Word[];
  deselectedWords: Set<string>;
  onToggleWord: (key: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onClose: () => void;
  onGenerateCategory?: (category: string) => void;
  onFilterByCategory?: (category: string) => void;
  generating?: boolean;
  learningLanguage?: LearningLanguage;
  language?: Language;
}

export default function WordPickerModal({
  category,
  words,
  deselectedWords,
  onToggleWord,
  onSelectAll,
  onDeselectAll,
  onClose,
  onGenerateCategory,
  onFilterByCategory,
  generating,
  learningLanguage = 'pl',
  language = 'en',
}: WordPickerModalProps) {
  if (!category) return null;

  const total = words.length;
  let selected = 0;
  for (const w of words) {
    if (!deselectedWords.has(`${category}::${w.pl}`)) selected++;
  }

  return (
    <div
      id="modal-overlay"
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <div className="modal-header">
          <h3 id="modal-title">{category}</h3>
          <button id="modal-close" className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-actions">
          <button id="modal-select-all" className="btn btn-sm" onClick={onSelectAll}>
            {t(language, 'btn.selectAll')}
          </button>
          <button
            id="modal-deselect-all"
            className="btn btn-sm btn-outline"
            onClick={onDeselectAll}
          >
            {t(language, 'btn.deselectAll')}
          </button>
          <span id="modal-count" className="badge">
            {selected}/{total}
          </span>
          {onGenerateCategory && (
            <button
              className="btn btn-primary btn-sm"
              disabled={generating || selected === 0}
              onClick={() => onGenerateCategory(category!)}
              title={t(language, 'modal.generate')}
            >
              ✨
            </button>
          )}
          {onFilterByCategory && (
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onFilterByCategory(category!)}
              title={t(language, 'modal.filterSentences')}
            >
              🔎
            </button>
          )}
        </div>
        <div id="modal-words" className="modal-words">
          {words.map((w) => {
            const key = `${category}::${w.pl}`;
            const checked = !deselectedWords.has(key);
            return (
              <div className="word-row" key={key}>
                <input
                  type="checkbox"
                  checked={checked}
                  data-key={key}
                  onChange={() => onToggleWord(key)}
                />
                <span className="word-pl">{getSourceWordLabel(w, learningLanguage)}</span>
                <span className="word-ru">{w.ru}</span>
                <span className="word-pos">{w.pos}</span>
              </div>
            );
          })}
        </div>
        <div className="modal-footer">
          <button id="modal-done" className="btn btn-primary" onClick={onClose}>
            {t(language, 'btn.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
